import { useEffect, useState } from 'react';
import { fetchFeaturedPosts } from '../api';

// Public "Featured on Instagram" section.
//
// Instagram no longer lets us embed posts reliably from a third-party site
// (their embed.js hydration silently fails, direct iframes get X-Frame-Options:
// DENY, and the post HTML is login-walled to scrapers). So instead of depending
// on Instagram at page load, we display the post's photo — uploaded by the admin
// and hosted in our own blob storage — as a tile that links out to the real
// post. Reliable, fast, and it still drives clicks to Instagram.

export default function InstagramFeed() {
  const [posts, setPosts] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    fetchFeaturedPosts()
      .then((res) => { if (!cancelled) setPosts(res.posts || []); })
      .catch(() => { if (!cancelled) setPosts([]); });
    return () => { cancelled = true; };
  }, []);

  // Only show posts that actually have an image to display.
  const shown = (posts || []).filter((p) => p.imageUrl);
  if (!posts || shown.length === 0) return null;

  return (
    <section className="lp-section lp-instagram" id="instagram">
      <h2 className="lp-h2">Featured on Instagram</h2>
      <p className="lp-subtle">A few favorites from our feed — tap to view on Instagram.</p>

      <div className="ig-tile-grid">
        {shown.map((post) => (
          <a
            key={post.shortcode}
            className="ig-tile"
            href={post.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={post.caption ? `Instagram: ${post.caption}` : 'View post on Instagram'}
          >
            <img className="ig-tile-img" src={post.imageUrl} alt={post.caption || 'Instagram post'} loading="lazy" />
            <span className="ig-tile-badge" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M12 2.2c3.2 0 3.6 0 4.9.07 1.17.05 1.8.25 2.23.42.56.22.96.48 1.38.9.42.42.68.82.9 1.38.17.42.37 1.06.42 2.23.06 1.27.07 1.65.07 4.85s0 3.58-.07 4.85c-.05 1.17-.25 1.8-.42 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.17-1.06.37-2.23.42-1.27.06-1.65.07-4.85.07s-3.58 0-4.85-.07c-1.17-.05-1.8-.25-2.23-.42a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.17-.42-.37-1.06-.42-2.23C2.21 15.58 2.2 15.2 2.2 12s0-3.58.07-4.85c.05-1.17.25-1.8.42-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.17 1.06-.37 2.23-.42C8.42 2.21 8.8 2.2 12 2.2Zm0 1.8c-3.15 0-3.5 0-4.75.07-.9.04-1.38.19-1.7.32-.43.16-.74.36-1.06.68-.32.32-.52.63-.68 1.06-.13.32-.28.8-.32 1.7C3.42 8.5 3.4 8.85 3.4 12s0 3.5.07 4.75c.04.9.19 1.38.32 1.7.16.43.36.74.68 1.06.32.32.63.52 1.06.68.32.13.8.28 1.7.32 1.25.06 1.6.07 4.75.07s3.5 0 4.75-.07c.9-.04 1.38-.19 1.7-.32.43-.16.74-.36 1.06-.68.32-.32.52-.63.68-1.06.13-.32.28-.8.32-1.7.06-1.25.07-1.6.07-4.75s0-3.5-.07-4.75c-.04-.9-.19-1.38-.32-1.7a2.85 2.85 0 0 0-.68-1.06 2.85 2.85 0 0 0-1.06-.68c-.32-.13-.8-.28-1.7-.32C15.5 4 15.15 4 12 4Zm0 3.06A4.94 4.94 0 1 1 12 16.94 4.94 4.94 0 0 1 12 7.06Zm0 1.8a3.14 3.14 0 1 0 0 6.28 3.14 3.14 0 0 0 0-6.28Zm5.15-.63a1.15 1.15 0 1 1-2.3 0 1.15 1.15 0 0 1 2.3 0Z" />
              </svg>
            </span>
            {post.caption ? <span className="ig-tile-caption">{post.caption}</span> : null}
          </a>
        ))}
      </div>
    </section>
  );
}
